import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { AUTO_RESET_TIMEOUT } from '../../utils/constants';

const QRCodeScreen = ({ imageUrl, onDone, onAutoReset }) => {
  const [timeRemaining, setTimeRemaining] = useState(AUTO_RESET_TIMEOUT / 1000);

  useEffect(() => {
    // Auto-reset timer
    const autoResetTimer = setTimeout(() => {
      onAutoReset();
    }, AUTO_RESET_TIMEOUT);

    // Countdown display timer
    const countdownInterval = setInterval(() => {
      setTimeRemaining((prev) => {
        const newTime = prev - 1;
        if (newTime <= 0) {
          clearInterval(countdownInterval);
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => {
      clearTimeout(autoResetTimer);
      clearInterval(countdownInterval);
    };
  }, [onAutoReset]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-8 py-12">
      <div className="max-w-3xl w-full space-y-10 text-center">
        {/* Title */}
        <div>
          <h1 className="text-6xl font-bold text-white mb-4 animate-pulse">
            Scan to Take Home!
          </h1>
          <p className="text-3xl text-gray-200">
            Your photo is ready to download
          </p>
        </div>

        {/* QR Code */}
        <div className="flex justify-center">
          <div className="relative bg-white p-8 rounded-3xl shadow-2xl">
            <QRCodeSVG
              value={imageUrl}
              size={400}
              level="H"
              includeMargin={true}
              imageSettings={{
                src: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ctext y='75' font-size='80'%3E✨%3C/text%3E%3C/svg%3E",
                height: 40,
                width: 40,
                excavate: true,
              }}
            />

            {/* Decorative corners */}
            <div className="absolute -top-3 -left-3 w-16 h-16 border-t-4 border-l-4 border-[#e5181e] rounded-tl-xl"></div>
            <div className="absolute -top-3 -right-3 w-16 h-16 border-t-4 border-r-4 border-[#e5181e] rounded-tr-xl"></div>
            <div className="absolute -bottom-3 -left-3 w-16 h-16 border-b-4 border-l-4 border-[#e5181e] rounded-bl-xl"></div>
            <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b-4 border-r-4 border-[#e5181e] rounded-br-xl"></div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
          <div className="space-y-4 text-left">
            <div className="flex items-start gap-4">
              <span className="text-4xl">📱</span>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Step 1</h3>
                <p className="text-xl text-gray-200">Open your phone camera</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <span className="text-4xl">📷</span>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Step 2</h3>
                <p className="text-xl text-gray-200">Point at the QR code</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <span className="text-4xl">💾</span>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Step 3</h3>
                <p className="text-xl text-gray-200">Tap the link to view and download</p>
              </div>
            </div>
          </div>
        </div>

        {/* Timer Warning */}
        <div className="bg-yellow-500/20 backdrop-blur-sm rounded-2xl px-6 py-4 border border-yellow-500/50">
          <p className="text-2xl text-yellow-200">
            ⏱️ Auto-reset in <span className="font-bold text-white">{timeRemaining}s</span>
          </p>
        </div>

        {/* Done Button */}
        <button
          onClick={onDone}
          className="px-16 py-6 bg-[#e5181e] text-white rounded-full font-bold text-3xl hover:bg-[#c01419] transition-all transform hover:scale-105 active:scale-95 shadow-2xl"
        >
          Done ✓
        </button>

        <p className="text-lg text-gray-300">
          Tap "Done" when you're finished to help the next person
        </p>
      </div>
    </div>
  );
};

export default QRCodeScreen;
